import { createContext, useContext } from 'react';

import { type VariantProps, tv } from 'tailwind-variants';

import type * as SelectPrimitive from '@radix-ui/react-select';

export const selectVariants = tv({
  slots: {
    trigger: [
      'unstyled-button outline-none',
      'flex box-border items-center justify-between gap-2',
      'rounded-sm bg-transparent',
      'text-base whitespace-nowrap',
      '*:data-[slot=select-value]:line-clamp-1',
      '*:data-[slot=select-value]:flex',
      '*:data-[slot=select-value]:items-center',
      '*:data-[slot=select-value]:text-base',
      '*:data-[slot=select-value]:gap-2',
      '*:data-[slot=select-value]:truncate',
      '[&_svg]:shrink-0',
      "[&_svg:not([class*='size-'])]:size-4",
      '[&_svg[data-trigger-icon]]:text-muted-foreground',
      '[&_svg[data-trigger-icon]]:hover:text-foreground',
      'aria-invalid:ring-destructive/20',
      'aria-invalid:border-destructive',
      'disabled:**:data-[slot=select-value]:text-muted-foreground',
    ],
    content: [
      'bg-popover text-popover-foreground',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'data-[side=bottom]:slide-in-from-top-2',
      'data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2',
      'data-[side=top]:slide-in-from-bottom-2',
      'relative z-3000 max-h-(--radix-select-content-available-height)',
      'min-w-[8rem] origin-(--radix-select-content-transform-origin)',
      'overflow-x-hidden overflow-y-auto',
      'rounded-sm base-border shadow-md',
    ],
    viewport: 'box-border p-1',
    label: 'text-muted-foreground px-2 py-1.5 text-sm',
    itemLabel: 'text-base truncate block min-w-0',
    item: [
      'box-border hover:cursor-pointer',
      'data-[state=checked]:text-primary',
      'relative flex w-full cursor-default items-center gap-2 min-w-0',
      'rounded-sm py-1.5 pr-8 pl-2 text-base',
      'outline-hidden select-none',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      '[&_svg]:pointer-events-none [&_svg]:shrink-0',
      "[&_svg:not([class*='size-'])]:size-4",
      '*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2',
      'data-[highlighted=true]:bg-indigo-30 data-[highlighted=true]:text-primary',
    ],
    itemIndicator: 'absolute right-2 flex size-3.5 items-center justify-center',
    separator: 'bg-border pointer-events-none -mx-1 my-1 h-px',
    scrollButton: 'flex cursor-default items-center justify-center py-1',
    searchContainer: '-mx-1 base-border-b p-1',
    searchContainerInner: 'px-2 py-1',
    noResults: 'p-2 text-sm text-muted-foreground text-center',
    optionsList: 'overflow-y-auto pt-1',
    selectedContainer: 'flex-wrap gap-1 min-h-0 py-1 overflow-y-auto',
    placeholder: 'flex items-center gap-2',
  },
  variants: {
    variant: {
      default: {
        trigger: 'input-border input-hover input-disabled pl-3 pr-2',
      },
      unstyled: {
        trigger: '',
      },
    },
    sizeFull: {
      true: {
        trigger: '!size-full',
      },
      false: {
        trigger: '',
      },
    },
    fullWidth: {
      true: {
        trigger: '!w-full',
      },
      false: {
        trigger: '',
      },
    },
    isMultiSelect: {
      true: 'h-auto',
      false: '',
    },
    size: {
      small: {
        trigger: 'h-7',
        placeholder: '[&_svg]:!size-3.5',
      },
      default: {
        trigger: 'h-8',
        placeholder: '[&_svg]:!size-3.5',
      },
    },
    placeholderVariant: {
      default: {
        trigger: 'data-[placeholder]:text-placeholder',
        placeholder: 'text-placeholder',
      },
      light: {
        trigger: 'data-[placeholder]:text-placeholder-light',
        placeholder: 'text-placeholder-light',
      },
    },
    position: {
      'item-aligned': {
        content: '',
        viewport: '',
      },
      popper: {
        content: [
          'data-[side=bottom]:translate-y-1',
          'data-[side=left]:-translate-x-1',
          'data-[side=right]:translate-x-1',
          'data-[side=top]:-translate-y-1',
        ],
        viewport: ['h-[var(--radix-select-trigger-height)]', 'w-full', 'scroll-my-1'],
      },
    },
    fitTrigger: {
      true: {
        content: 'min-w-[var(--radix-select-trigger-width)]',
      },
    },
  },
  compoundVariants: [
    {
      variant: 'unstyled',
      class: {
        trigger: 'h-auto border-none p-0',
      },
    },
    {
      isMultiSelect: true,
      size: 'default',
      variant: 'default',
      class: {
        trigger: 'h-auto min-h-8',
      },
    },
    {
      isMultiSelect: true,
      size: 'small',
      variant: 'default',
      class: {
        trigger: 'h-auto min-h-7',
      },
    },
  ],
  defaultVariants: {
    size: 'default',
    position: 'popper',
    isMultiSelect: false,
    variant: 'default',
    placeholderVariant: 'default',
    fitTrigger: true,
  },
});

export type SelectBaseOption = {
  type?: 'option';
  value: string;
  label: React.ReactNode;
  searchLabel?: string;
  disabled?: boolean;
};

export type SelectGroupOption = {
  type: 'group';
  groupLabel: React.ReactNode;
  options: SelectBaseOption[];
};

export type SelectSeparatorOption = {
  type: 'separator';
};

export type SelectOptionItem = SelectBaseOption | SelectGroupOption | SelectSeparatorOption;

type BaseSelectProps = Omit<VariantProps<typeof selectVariants>, 'isMultiSelect' | 'placeholder'> &
  SelectContentProps & {
    disabled?: boolean;
    allowClear?: boolean;
    isSearchable?: boolean;
    onSearchChange?: (search: string) => void;
    findMatch?: (options: SelectBaseOption[], search: string) => SelectOptionItem[];
    children?: React.ReactElement<SelectCustomTriggerProps>;
    options: SelectOptionItem[];
    isLoading?: boolean;
    hideChevron?: boolean;
    placeholder?: React.ReactNode;
  };

export interface SingleSelectProps extends BaseSelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string | undefined) => void;
  maxVisible?: never;
  isMultiSelect?: false;
  hideSelection?: never;
  renderBadge?: never;
}

export interface MultiSelectProps extends BaseSelectProps {
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  maxVisible?: number;
  hideSelection?: boolean;
  isMultiSelect: true;
  renderBadge?: RenderBadge;
}

export type RenderBadge = (option: SelectBaseOption, onRemove: () => void) => React.ReactNode;

export type SelectProps =
  | (SingleSelectProps & { isMultiSelect?: false | undefined })
  | (MultiSelectProps & { isMultiSelect: true });

export type SelectTriggerProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>;

export type SelectCustomTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
>;

export type SelectCurrentValueProps = {
  placeholder?: React.ReactNode;
  renderBadge?: RenderBadge;
  hideSelection?: boolean;
};

export type SelectContentProps = Omit<
  React.ComponentProps<typeof SelectPrimitive.Content>,
  'className' | 'children' | 'placeholder'
> & {
  searchPlaceholder?: string;
  emptyPlaceholder?: string;
};

export type SelectContextValue = (
  | {
      isMultiSelect: false;
      value: string | undefined;
      onValueChange?: (value: string | undefined) => void; // Changed
      maxVisible?: never;
    }
  | {
      isMultiSelect: true;
      value: string[];
      onValueChange?: (value: string[]) => void;
      maxVisible?: number;
    }
) & {
  open: boolean;
  allowClear: boolean;
  isSearchable?: boolean;
  searchQuery?: string;
  onClearValue: () => void;
  onToggleValue: (value: string) => void;
  onSearchChange?: (search: string) => void;
  findMatch?: (options: SelectBaseOption[], search: string) => SelectOptionItem[];
  styleVariants: StyleVariants;
  emptyPlaceholder?: string;
  options: SelectOptionItem[];
  hideChevron: boolean;
  isLoading: boolean;
  selectedOptionsCache: React.MutableRefObject<Map<string, SelectBaseOption>>;
};

export type StyleVariants = VariantProps<typeof selectVariants>;

export const SelectContext = createContext<SelectContextValue>({
  open: false,
  options: [],
  styleVariants: {
    size: 'default',
    position: 'popper',
    fullWidth: false,
    sizeFull: false,
    isMultiSelect: false,
    placeholderVariant: 'default',
    fitTrigger: true,
  },

  isMultiSelect: false,
  value: '',
  onValueChange: () => {},
  onToggleValue: () => {},
  onClearValue: () => {},
  onSearchChange: () => {},
  isLoading: false,
  allowClear: false,
  isSearchable: false,
  searchQuery: '',
  hideChevron: false,
  selectedOptionsCache: { current: new Map() },
});

export const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('useSelect must be used within a Select');
  }
  return context;
};
